import React from "react";
import { Dialog } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import SubmitButton from "components/common/SubmitButton";
import { IoAlertCircleOutline } from "react-icons/io5";

const CustomTitle = ({ plural }) => (
  <div
    style={{
      fontSize: "16px",
      lineHeight: "1.3em",
      color: "black",
      display: "flex",
      alignItems: "center",
    }}
  >
    <IoAlertCircleOutline className="me-2" size={20} color="#f59e0b" />
    {plural ? "Emails Already Attached" : "Email Already Attached"}
  </div>
);

function sanitizeText(value) {
  if (!value) return "";
  let s = String(value);
  s = s.replace(
    /=\?([^?]+)\?([BbQq])\?([^?]*)\?=/g,
    (_, charset, enc, payload) => {
      try {
        if (enc.toLowerCase() === "b") {
          const decoded = atob(payload.replace(/\s+/g, ""));
          const bytes = Uint8Array.from(decoded, (c) => c.charCodeAt(0));
          return new TextDecoder(charset || "utf-8", { fatal: false }).decode(bytes);
        }
        return payload
          .replace(/_/g, " ")
          .replace(/=([0-9A-Fa-f]{2})/g, (_m, h) => String.fromCharCode(parseInt(h, 16)));
      } catch {
        return "";
      }
    },
  );
  s = s.replace(/[\x00-\x08\x0B\x0C\x0E-\x1F\x7F]/g, "");
  return s.trim();
}

function truncate(value, max) {
  if (!value) return "";
  return value.length > max ? value.slice(0, max - 1).trimEnd() + "…" : value;
}

export const EmailAlreadyAttachedDialog = ({
  open,
  existingMatterRef,
  existingMatterDetails,
  newMatterRef,
  emailSubject,
  attachedItems,
  onClose,
  onConfirm,
  loading,
}) => {
  if (!open) return null;

  const cleanNewMatterRef = sanitizeText(newMatterRef) || "this matter";
  const items = (Array.isArray(attachedItems) && attachedItems.length > 0
    ? attachedItems
    : [
        {
          subject: emailSubject,
          matterRef: existingMatterRef,
          matterDetails: existingMatterDetails,
        },
      ]
  ).map((it) => ({
    subject: truncate(sanitizeText(it?.subject), 80),
    matterRef: sanitizeText(it?.matterRef) || "Unknown",
    matterDetails: sanitizeText(it?.matterDetails),
  }));
  const isMulti = items.length > 1;

  return (
    <Dialog title={<CustomTitle plural={isMulti} />} onClose={onClose} width={520}>
      <div className="p-4">
        <div className="mb-3 text-muted small">
          {isMulti
            ? `${items.length} of the selected emails are already attached:`
            : "This email is already attached to:"}
        </div>

        <div
          className="d-flex flex-column gap-2"
          style={{ maxHeight: 280, overflowY: "auto" }}
        >
          {items.map((it, idx) => (
            <div
              key={idx}
              className="rounded p-3 d-flex flex-column"
              style={{ background: "#f6f8fb", border: "1px solid #eef0f4" }}
            >
              {it.subject && (
                <span className="fw-medium text-truncate mb-1">
                  {it.subject}
                </span>
              )}
              <span className="fw-semibold">{it.matterRef}</span>
              {it.matterDetails && (
                <span className="text-muted small">{it.matterDetails}</span>
              )}
            </div>
          ))}
        </div>

        <div className="mt-3">
          Attach{isMulti ? " them all" : ""} to{" "}
          <span className="fw-semibold">{cleanNewMatterRef}</span>?
        </div>
      </div>

      <div className="d-flex justify-content-end w-100 gap-2 dialog-footer">
        <Button
          className="common-btn bgBtnSecondary"
          onClick={onClose}
          disabled={loading}
        >
          Cancel
        </Button>
        <SubmitButton
          className="common-btn me-3"
          onClick={onConfirm}
          isLoading={loading}
          disabled={loading}
        >
          Attach
        </SubmitButton>
      </div>
    </Dialog>
  );
};

export default EmailAlreadyAttachedDialog;
